import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFinancialWidgets } from '@/hooks/use-financial-widgets';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface TopAssetsByValueCardProps {
  readonly className?: string;
}

export function TopAssetsByValueCard({ className }: TopAssetsByValueCardProps): React.ReactElement {
  const navigate = useNavigate();
  const { useTopAssetsByValue } = useFinancialWidgets();
  const { data, isLoading, error } = useTopAssetsByValue(5);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'in use':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'retired':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const handleAssetClick = (assetId: number) => {
    navigate(`/assets/${assetId}`);
  };

  if (isLoading) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm bg-white dark:bg-black', className)}>
        <CardHeader>
          <CardTitle className="text-base font-semibold tracking-tight">Top Assets by Value</CardTitle>
          <CardDescription>Highest value assets in portfolio</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {['skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4', 'skeleton-5'].map((id) => (
              <Skeleton key={id} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data || data.assets.length === 0) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm bg-white dark:bg-black', className)}>
        <CardHeader>
          <CardTitle className="text-base font-semibold tracking-tight">Top Assets by Value</CardTitle>
          <CardDescription>Highest value assets in portfolio</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">No asset data available.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col', className)}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Top Assets by Value</CardTitle>
            <CardDescription>Highest value assets in portfolio</CardDescription>
          </div>
          <div className="p-2 bg-amber-50 dark:bg-amber-950 rounded-lg">
            <Trophy className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="space-y-3">
          {/* Asset List - Compact */}
          <div className="space-y-1.5">
            {data.assets.map((asset, index) => (
              <button
                type="button"
                key={asset.id}
                className="p-2.5 rounded-md border bg-card hover:shadow-sm transition-all cursor-pointer group w-full text-left"
                onClick={() => handleAssetClick(asset.id)}
              >
                <div className="flex items-center justify-between gap-3">
                  {/* Rank & Info */}
                  <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-sm truncate">{asset.name}</h4>
                        <Badge variant="outline" className={cn('text-xs py-0 h-4', getStatusColor(asset.status))}>
                          {asset.status}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">{asset.category}</div>
                    </div>
                  </div>

                  {/* Value & Percentage */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-sm font-semibold">{formatCurrency(asset.currentValue)}</div>
                      <div className="text-xs text-muted-foreground">
                        {data.totalValue > 0 ? ((asset.currentValue / data.totalValue) * 100).toFixed(1) : 0}%
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* View All Button */}
          {data.assets.length >= 5 && (
            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => navigate('/assets')}
              >
                View All Assets
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
